import React from 'react';
import Modal from '../modal/Modal';

/**
 * The save step: every value about to change, old to new.
 *
 * A configuration save is not one decision but a batch of them, made minutes
 * apart while scrolling a long page. Listing them back is what lets an operator
 * catch the field they nudged and forgot — the failure a plain "Save" button
 * cannot protect against, and the reason this modal lists rather than counts.
 *
 * @param {Object} props
 * @param {Array<{label: string, from: string, to: string}>} props.changes
 * @param {boolean} [props.pending] - While the request is in flight.
 * @param {React.RefObject<HTMLElement>} [props.contentRef] - The page's content column,
 *   so the dialog centres on it rather than on the window.
 * @param {function(): void} props.onConfirm
 * @param {function(): void} props.onCancel
 * @returns {JSX.Element}
 */
export default function SaveDiffModal({
  changes, pending, contentRef, onConfirm, onCancel,
}) {
  const count = changes.length;
  const noun = count === 1 ? 'change' : 'changes';

  return (
    <Modal
      title={`Review ${count} ${noun} before saving`}
      className="gmpro-modal"
      contentRef={contentRef}
      testId="gmpro-save-modal"
    >
      <ul className="gmpro-modal__diff">
        {changes.map((change) => (
          <li className="gmpro-modal__change" key={change.label}>
            <span className="gmpro-modal__change-label">{change.label}</span>
            <span className="gmpro-modal__change-values">
              <span className="gmpro-modal__from">{change.from}</span>
              <span className="gmpro-modal__arrow"> → </span>
              <span className="gmpro-modal__to">{change.to}</span>
            </span>
          </li>
        ))}
      </ul>

      <div className="gmpro-modal__actions">
        <button
          type="button"
          className="gmpro-modal__cancel"
          onClick={onCancel}
          disabled={pending}
        >
          Cancel
        </button>
        <button
          type="button"
          className="gmpro-modal__confirm"
          onClick={onConfirm}
          disabled={pending}
          data-testid="gmpro-save-accept"
        >
          {pending ? 'Saving…' : `Save ${count} ${noun}`}
        </button>
      </div>
    </Modal>
  );
}
